import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer } from '@/features/admin/ui';
import { useAuth } from '@/lib/auth/useAuth';
import { adminQueries, auditLog, notify } from '@/lib/queries/admin';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/corrections')({
  component: CorrectionsPage,
});

const EDIT_WINDOW_MS = 60 * 60 * 1000;

function CorrectionsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [composing, setComposing] = useState(false);

  const list = useQuery(adminQueries.corrections(search));
  const rows = list.data ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <AdminHeader kicker="Admin · Accountability" title="Corrections" />
        <button
          type="button"
          onClick={() => setComposing(true)}
          className="shrink-0 rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          New correction
        </button>
      </div>

      <input
        type="search"
        placeholder="Search by article title"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mt-6 w-full max-w-sm rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">No corrections recorded.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {rows.map((c) => (
            <li key={c.id} className="rounded-md border border-ink/10 bg-bg-light/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-serif text-base text-ink">
                  {c.content?.title ?? 'Unknown article'}
                </p>
                {c.is_significant && (
                  <span className="rounded-sm bg-oxblood/10 px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] text-oxblood">
                    Significant
                  </span>
                )}
              </div>
              <p className="mt-1 font-sans text-xs text-ink/50">
                {new Date(c.created_at).toLocaleString()}
              </p>
              <p className="mt-2 whitespace-pre-wrap font-sans text-sm text-ink/80">
                {c.correction_text}
              </p>
              {Date.now() - new Date(c.created_at).getTime() < EDIT_WINDOW_MS && (
                <InlineEdit
                  id={c.id}
                  initial={c.correction_text}
                  onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'corrections'] })}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <Drawer open={composing} onClose={() => setComposing(false)} title="New correction">
        <ComposeForm
          authorId={user?.id}
          onDone={() => {
            setComposing(false);
            void qc.invalidateQueries({ queryKey: ['admin', 'corrections'] });
          }}
        />
      </Drawer>
    </div>
  );
}

function InlineEdit({
  id,
  initial,
  onSaved,
}: {
  id: string;
  initial: string;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(initial);
  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('corrections')
        .update({ correction_text: text.trim() })
        .eq('id', id);
      if (error) throw error;
      await auditLog('correction.edited', 'corrections', id, {});
    },
    onSuccess: () => {
      toast.success('Correction updated.');
      setEditing(false);
      onSaved();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-2 font-sans text-xs text-oxblood underline"
      >
        Edit (typo window — under 1 hour)
      </button>
    );
  }
  return (
    <div className="mt-3 space-y-2">
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          disabled={save.isPending || !text.trim()}
          onClick={() => save.mutate()}
          className="rounded-md bg-ink px-3 py-1.5 font-sans text-xs text-parchment hover:bg-oxblood disabled:opacity-60"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs text-ink"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ComposeForm({ authorId, onDone }: { authorId: string | undefined; onDone: () => void }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<{
    id: string;
    title: string;
    slug: string;
    creator_id: string;
  } | null>(null);
  const [text, setText] = useState('');
  const [significant, setSignificant] = useState(false);
  const [notifyCreator, setNotifyCreator] = useState(true);

  const lookup = useQuery(adminQueries.publishedContentLookup(query));

  const submit = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Select an article.');
      if (!text.trim()) throw new Error('Correction text is required.');
      if (!authorId) throw new Error('Not authenticated.');
      const { data, error } = await supabase
        .from('corrections')
        .insert({
          content_id: selected.id,
          correction_text: text.trim(),
          is_significant: significant,
          created_by: authorId,
        })
        .select('id')
        .single();
      if (error) throw error;

      await auditLog('correction.created', 'corrections', data.id, {
        content_id: selected.id,
        significant,
      });

      if (notifyCreator) {
        // Resolve the creator's display name for the greeting.
        const { data: prof } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', selected.creator_id)
          .maybeSingle();
        await notify('correction_published', null, {
          name: prof?.display_name ?? null,
          contentTitle: selected.title,
          correctionText: text.trim(),
          articleUrl: `${import.meta.env.PUBLIC_MARKETING_URL ?? ''}/article/${selected.slug}`,
        });
      }
      // Section C wires the marketing rebuild trigger; log the intent for now.
      console.warn('marketing rebuild trigger (deferred to Section C)', {
        content_id: selected.id,
      });
    },
    onSuccess: () => {
      toast.success('Correction recorded.');
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  return (
    <div className="space-y-5">
      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-ink/10 bg-bg-light/40 p-3">
          <span className="font-serif text-sm text-ink">{selected.title}</span>
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="font-sans text-xs text-oxblood underline"
          >
            Change
          </button>
        </div>
      ) : (
        <div>
          <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
            Article
          </label>
          <input
            type="search"
            placeholder="Search published articles by title"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
          />
          {(lookup.data ?? []).length > 0 && (
            <ul className="mt-2 rounded-md border border-ink/10">
              {(lookup.data ?? []).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(c)}
                    className="block w-full px-3 py-2 text-left font-sans text-sm text-ink hover:bg-bg-light/60"
                  >
                    {c.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div>
        <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
          Correction
        </label>
        <textarea
          rows={5}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </div>

      <label className="flex items-center gap-2 font-sans text-sm text-ink/80">
        <input
          type="checkbox"
          checked={significant}
          onChange={(e) => setSignificant(e.target.checked)}
          className="accent-oxblood"
        />
        Significant — renders as a notice at the foot of the article, and marks the byline
        &ldquo;(Edited)&rdquo;
      </label>
      <label className="flex items-center gap-2 font-sans text-sm text-ink/80">
        <input
          type="checkbox"
          checked={notifyCreator}
          onChange={(e) => setNotifyCreator(e.target.checked)}
          className="accent-oxblood"
        />
        Notify the contributor by email
      </label>

      <button
        type="button"
        disabled={submit.isPending || !selected || !text.trim()}
        onClick={() => submit.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Record correction
      </button>
    </div>
  );
}
